import * as React from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { cn } from '@/shared/utils/styling';
import { useScrollDetection } from '@/shared/lib';

export type StickyColumnConfig = {
  index?: number;
  width?: {
    mobile: string;
    desktop: string;
  };
  gradientColor?: string;
};

interface StickyTableContextValue {
  stickyColumnIndex: number;
  stickyColumnWidth: { mobile: string; desktop: string };
  hasScroll: boolean;
  gradientColor: string;
  getStickyClassName: (columnIndex: number) => string;
}

const StickyTableContext = React.createContext<StickyTableContextValue | null>(
  null
);

const useStickyTableContext = () => {
  return React.useContext(StickyTableContext);
};

interface TableVariantContextValue {
  variant: 'default' | 'simple';
}

const TableVariantContext = React.createContext<TableVariantContextValue>({
  variant: 'simple',
});

const useTableVariantContext = () => {
  return React.useContext(TableVariantContext);
};

const tableRowStyles = tv({
  base: 'transition-colors',
  variants: {
    variant: {
      default: 'group cursor-pointer border-none',
      simple: 'hover:bg-muted/50 data-[state=selected]:bg-muted border-none',
    },
    isEven: {
      true: 'border-none',
      false: 'border-none',
    },
  },
  compoundVariants: [
    {
      variant: 'simple',
      isEven: true,
      class: 'bg-transparent',
    },
    {
      variant: 'simple',
      isEven: false,
      class: 'bg-transparent',
    },
  ],
  defaultVariants: {
    variant: 'simple',
    isEven: false,
  },
});

const tableCellStyles = tv({
  base: '',
  variants: {
    variant: {
      default: '',
      simple: '',
    },
    isEven: {
      true: 'bg-tertiary-light dark:bg-card group-hover:bg-muted group-hover:dark:bg-muted',
      false: 'bg-card group-hover:bg-muted',
    },
  },
  compoundVariants: [
    {
      variant: 'simple',
      isEven: true,
      class: 'bg-transparent',
    },
    {
      variant: 'simple',
      isEven: false,
      class: 'bg-transparent',
    },
  ],
  defaultVariants: {
    variant: 'simple',
    isEven: false,
  },
});

const stickyColumnStyles = tv({
  base: 'sticky left-0 z-10',
  variants: {
    hasScroll: {
      true: 'after:content-[""] after:block after:absolute after:top-0 after:-right-[1rem] after:bottom-0 after:w-[1rem] after:pointer-events-none [&::after]:bg-[linear-gradient(90deg,var(--sticky-gradient-color)_0%,transparent_100%)]',
      false: '',
    },
  },
  defaultVariants: {
    hasScroll: false,
  },
});

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  className?: string;
  stickyColumn?: StickyColumnConfig;
  variant?: 'default' | 'simple';
};

type TableSectionProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  className?: string;
};

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  className?: string;
  rowIndex?: number;
};

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
  stickyColumnIndex?: number;
};

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
  stickyColumnIndex?: number;
  rowIndex?: number;
};

type TableCaptionProps = React.HTMLAttributes<HTMLTableCaptionElement> & {
  className?: string;
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    { className, stickyColumn, variant = 'default', children, ...props },
    ref
  ) => {
    const tableRef = useRef<HTMLTableElement>(null);
    const { ref: tableScrollRef } = useScrollDetection<HTMLDivElement>({
      trackHorizontal: true,
      trackVertical: false,
    });

    const getHasTableScroll = () => {
      if (!tableScrollRef.current) return false;
      return (
        tableScrollRef.current.scrollWidth > tableScrollRef.current.clientWidth
      );
    };

    const [hasTableScroll, setHasTableScroll] = useState(() =>
      getHasTableScroll()
    );

    const combinedRef = React.useCallback(
      (node: HTMLTableElement | null) => {
        tableRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    useLayoutEffect(() => {
      if (!tableRef.current) return;

      const scrollContainer = tableRef.current.parentElement;
      if (scrollContainer && scrollContainer instanceof HTMLDivElement) {
        (
          tableScrollRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = scrollContainer;
      }

      if (!stickyColumn) {
        setHasTableScroll(false);
        return;
      }

      const hasScroll = getHasTableScroll();
      setHasTableScroll(hasScroll);
    });

    const getStickyClassName = useCallback(
      (columnIndex: number) => {
        if (!stickyColumn || columnIndex !== stickyColumn.index) {
          return '';
        }

        const width = stickyColumn.width || {
          mobile: '16.8rem',
          desktop: '30rem',
        };

        const widthClasses = `w-[${width.mobile}] min-w-[${width.mobile}] max-w-[${width.mobile}] sm:w-[${width.desktop}] sm:min-w-[${width.desktop}] sm:max-w-[${width.desktop}]`;

        const stickyClasses = stickyColumnStyles({
          hasScroll: hasTableScroll,
        });

        return cn(stickyClasses, widthClasses);
      },
      [stickyColumn, hasTableScroll]
    );

    const contextValue: StickyTableContextValue | null = stickyColumn
      ? {
          stickyColumnIndex: stickyColumn.index ?? 0,
          stickyColumnWidth: stickyColumn.width || {
            mobile: '16.8rem',
            desktop: '30rem',
          },
          hasScroll: hasTableScroll,
          gradientColor: stickyColumn.gradientColor || 'rgba(24,26,33,0.06)',
          getStickyClassName,
        }
      : null;

    return (
      <TableVariantContext.Provider value={{ variant }}>
        <StickyTableContext.Provider value={contextValue}>
          <div className="relative w-full overflow-auto">
            <table
              ref={combinedRef}
              className={cn('w-full caption-bottom text-sm', className)}
              {...props}>
              {children}
            </table>
          </div>
        </StickyTableContext.Provider>
      </TableVariantContext.Provider>
    );
  }
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  TableSectionProps
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, TableSectionProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  TableSectionProps
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, rowIndex, ...props }, ref) => {
    const { variant } = useTableVariantContext();
    const isEven = rowIndex !== undefined ? rowIndex % 2 === 0 : false;

    const styles = tableRowStyles({
      variant,
      isEven: variant === 'default' ? isEven : false,
    });

    return <tr ref={ref} className={cn(styles, className)} {...props} />;
  }
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, stickyColumnIndex, ...props }, ref) => {
    const stickyContext = useStickyTableContext();

    const isSticky =
      stickyContext !== null &&
      stickyColumnIndex !== undefined &&
      stickyColumnIndex === stickyContext.stickyColumnIndex;

    const stickyClassName = isSticky
      ? stickyContext.getStickyClassName(stickyColumnIndex)
      : '';

    const gradientStyle: React.CSSProperties | undefined =
      isSticky && stickyContext.hasScroll
        ? ({
            '--sticky-gradient-color': stickyContext.gradientColor,
          } as React.CSSProperties)
        : undefined;

    return (
      <th
        ref={ref}
        className={cn(
          'h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
          stickyClassName,
          className
        )}
        style={gradientStyle}
        {...props}
      />
    );
  }
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, stickyColumnIndex, rowIndex, ...props }, ref) => {
    const stickyContext = useStickyTableContext();
    const { variant } = useTableVariantContext();
    const isEven = rowIndex !== undefined ? rowIndex % 2 === 0 : false;

    const isSticky =
      stickyContext !== null &&
      stickyColumnIndex !== undefined &&
      stickyColumnIndex === stickyContext.stickyColumnIndex;

    const stickyClassName = isSticky
      ? stickyContext.getStickyClassName(stickyColumnIndex)
      : '';

    const cellStyles = tableCellStyles({
      variant,
      isEven: variant === 'default' ? isEven : false,
    });

    const gradientStyle: React.CSSProperties | undefined =
      isSticky && stickyContext.hasScroll
        ? ({
            '--sticky-gradient-color': stickyContext.gradientColor,
          } as React.CSSProperties)
        : undefined;

    return (
      <td
        ref={ref}
        className={cn(
          'p-4 align-middle [&:has([role=checkbox])]:pr-0',
          stickyClassName,
          cellStyles,
          className
        )}
        style={gradientStyle}
        {...props}
      />
    );
  }
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  TableCaptionProps
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
